import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import CheckboxRountedIcon from '@mui/icons-material/CheckBoxRounded'
import DeleteIcon from '@mui/icons-material/Delete'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import React, { forwardRef, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import { useToast } from '../../../_components/Toast'
import { masterClassification, restApiKey, titleName, viewId } from '../../../_constants/Code'
import '../../../common/types'
import RelatedDashboard from '../parts/RelatedDashboard'
import { Draggable, Droppable, ModifyTree } from './extra/CertificationExtra'
import {
  noCategoryKey,
  rootNodeKey,
  useCategory,
  useCertificationData,
  useCertificationResources,
  useTree
} from './extra/useCertificationInfo'

import { useModal, useModalExtra } from '../../../_components/Modal'
import { PathInfoList } from '../../../_constants/PathInfoList'
import '../../../assets/css/common.css'
import '../../../assets/icon/style.css'
import { errorLog } from '../../../common/logger/Logger'
import { readCsvFile } from '../../../common/utils/FileUtils'
import { formatCommaDigit, parseCommaDigit } from '../../../common/utils/NumberUtils'
import { pJson } from '../../../common/utils/StringUtils'
import { httpPost } from '../../../services/api/AmplifyApi'
import { SpinnerButton } from '../../common/CustomButton'
import { getMessage } from '../../../common/utils/MessageUtils'
import { produce } from 'immer'

import { ReactComponent as UndoIcon } from '../../../assets/icon/undo.svg'
import { ReactComponent as CategoryIcon } from '../../../assets/icon/category.svg'
import Forbidden from '../../../_components/Forbidden'
import { ChrofyCustomSelect } from '../../common/ChrofyCustomSelect'
import _get from 'lodash/get'

const disallowedChars = [',', '\\', '"', '#', '.', '&', '+']
const replaceChars = (input, chars = [',', '\\', '"']) =>
  chars.reduce((col, current) => col.replaceAll(current, ''), input)

const checkIfCharsExist = (input = '', chars = disallowedChars) => {
  return chars.some((e) => String(input)?.includes(e))
}

/** @import '../../../common/types' */

const CertificationContent = (props) => {
  const { showModal, navigate } = props
  const [isLoaded, setIsLoaded] = useState(false)

  const [master, setMaster] = React.useState([])
  const [inputError, setInputError] = React.useState({})
  const [serverError, setServerError] = React.useState({})
  const [activeNode, setActiveNode] = useState(null)

  const errorMessageRef = React.useRef(null)

  const documentTitle = props.pageTitle || '資格マスター設定'

  const { addNodeToTree, removeNode, initTree } = useTree(props.corporationNumber)
  const { getMaster, getCertifications, checkIfCertificationIsInUse } = useCertificationResources(props)

  const mainState = useState(initTree())
  const [certificationMasterTree, setCertificationMasterTree] = mainState

  const init = async () => {
    document.title = documentTitle

    const master = await getMaster()
    setMaster(master)

    const { certifications } = await getCertifications()

    if (Object.keys(certifications).length > 0) {
      if (certifications[noCategoryKey]) {
        certifications[noCategoryKey].state.editable = false
        certifications[noCategoryKey].Metadata.Depth = 1
      }

      setCertificationMasterTree(certifications)
    }
    //
    setIsLoaded(true)
  }

  const { openUpdateCategoryModal, openCategoryModal } = useCategory(mainState)
  const { openCertificationModal, openUpdateCertificationModal } = useCertificationData(mainState)

  /**
   * @function onAddNodeToParent 親ノードに子を追加ためのモダル表示処理
   * @param {string} parentNodeId 親ノードのID
   * @param {'category' | string} category モダルの種別
   */
  const onAddNodeToParent = (parentNodeId, category) => {
    const parentNode = certificationMasterTree[parentNodeId || rootNodeKey]
    if (!parentNode) return

    if (category === 'category') {
      openCategoryModal(parentNode)
    } else {
      openCertificationModal(parentNode)
    }
  }

  /**
   * @function ツリーから子を削除
   * @param {string} nodeId ノードのID
   */
  const onRemoveNode = (nodeId) => {
    const tempState = { ...certificationMasterTree }
    setCertificationMasterTree(removeNode(tempState)(nodeId))
  }

  /**
   * @function onUpdateNode ノードデータ編集のためのモダルの表示処理
   * @param {string} parentNodeId 親ノードのID
   */
  const onUpdateNode = (nodeId) => {
    const node = certificationMasterTree[nodeId]
    if (!node) return

    if (node.Metadata.Depth < 3) openUpdateCategoryModal(node)
    else openUpdateCertificationModal(node)
  }

  /**
   * @function onFold カテゴリーの折り畳みの処理
   * @param {string} ノードのID
   */
  const onFold = (nodeId) => {
    const tree = { ...certificationMasterTree }

    tree[nodeId].state.fold = !tree[nodeId].state.fold
    setCertificationMasterTree(tree)
  }

  useEffect(() => {
    init()
  }, [])

  /** @function handleSaveCertification モダルの表示 */
  const handleSaveCertification = () => {
    showModal({
      title: 'マスター設定値登録の確認',
      description: (
        <>
          <p>設定値を登録してダッシュボードに反映します。よろしいでしょうか？</p>
          <p className="mt-3">
            ※ ダッシュボードへの反映に５分～10分程度かかります <br />
            マスター管理画面のステータスから反映状況を確認してください。
          </p>
        </>
      ),
      isWide: true,
      button: [
        {
          name: '実行',
          className: 'btn footer-btn button_base',
          click: async () => {
            await handleSavetoDB()
          }
        }
      ]
    })
  }

  /** @function handleCancel キャンセル処理 */
  const handleCancel = () => {
    navigate('/master/masterList')
  }

  /**
   * @function getAncestor 先祖を算出
   * @param {CertificationTree} tree
   * @param {string} currentNodeId
   * @returns {[string, string]} MainCategory名, SubCategory名
   */
  const getAncestors = (tree, currentNodeId) => {
    const noMainCategoryKey = '(メインカテゴリなし)'
    const noSubCategoryKey = '(サブカテゴリなし)'

    const currentNode = tree[currentNodeId]
    const parentNode = tree[currentNode.Metadata.SuperNode]

    if (parentNode.Metadata.Depth === 1) {
      const parentName =
        parentNode.node === noCategoryKey ? noMainCategoryKey : parentNode.CertificationData.CategoryName

      return [parentName, noSubCategoryKey]
    }

    const ancestorNode = tree[parentNode.Metadata.SuperNode]

    return [ancestorNode.CertificationData.CategoryName, parentNode.CertificationData.CategoryName]
  }

  /**
   * @param {CertificationTree} tree
   * @param {string} node
   */
  const getAncestorDepths = (tree, currentNodeId) => {
    let parent = tree[currentNodeId].Metadata.SuperNode
    const ancestors = []

    while (parent !== rootNodeKey) {
      ancestors.unshift(tree[parent].Metadata.DisplayOrder)
      parent = tree[parent].Metadata.SuperNode
    }

    if (ancestors.length < 3) {
      // サブがない場合は追加
      ancestors.splice(1, 0, 0)
    }

    return ancestors
  }

  /**
   * @function prepareFileData FileDataの算出
   * @param {CertificationNode[]} nodeList
   * @param {CertificationTree} tree
   */
  const prepareFileData = (nodeList, tree) => {
    const certifications = nodeList.reduce((acc, cert) => {
      const { CertificationCode = '', CertificationData = {}, Metadata, node } = cert
      const { Name, ValidityPeriod, HasScore } = CertificationData
      const { Depth, DisplayOrder } = Metadata || {}

      if (Depth < 3) return acc

      const [MainCategory = '', SubCategory = ''] = getAncestors(tree, node)

      const ancestorDepth = getAncestorDepths(tree, node)
      ancestorDepth.push(DisplayOrder)
      const combinedOrder = ancestorDepth.map((e) => String(e).padStart(3, '0')).join('')

      const validityPeriod = isNaN(Number(ValidityPeriod))
        ? ''
        : Number(ValidityPeriod) === 0
          ? ''
          : Number(ValidityPeriod)

      return [
        ...acc,
        {
          certification_code: CertificationCode,
          certification_name: Name,
          main_category: MainCategory,
          sub_category: SubCategory,
          validity_period: validityPeriod,
          has_score: HasScore ? '1' : '0',
          display_order: combinedOrder
        }
      ]
    }, [])
    const sorted = certifications
      .sort((a, b) => a.display_order.localeCompare(b.display_order))
      .map((e, index) => ({ ...e, display_order: index + 1 }))

    return sorted
  }

  const { _closeUpModal } = useModalExtra()

  /**
   * @function handleSavetoDB DBへ保存処理
   * @returns {Promise<void>}
   */
  const handleSavetoDB = async () => {
    const errorMessage = '更新処理でエラーが発生しました'
    try {
      const tree = { ...certificationMasterTree }

      const deletedNode = []

      for (const node in tree) {
        if (!tree[node].state.deleted) continue
        deletedNode.push(node)
      }

      const inUse = await checkIfCertificationIsInUse(deletedNode.map((e) => tree[e].CertificationCode))
      if (inUse) throw new Error('削除対象の資格は、他の設定で利用されているため削除できません。')

      deletedNode.forEach((e) => {
        const parentNodeId = tree[e].Metadata.SuperNode
        const parent = tree?.[parentNodeId]

        if (parent?.SubNode) parent.SubNode = parent?.SubNode.filter((x) => x !== e)
        delete tree[e]
      })

      const nodeList = Object.values(tree).map((node) => {
        if (node.SubNode) node.SubNode = node.SubNode.filter((e) => Boolean(tree[e]))
        return node
      })

      const fileData = prepareFileData(nodeList, tree)

      const init = {
        body: {
          request: {
            corporation_number: props.corporationNumber,
            corporation_name: props.corporationName,
            user_name: props.userName,
            user_sub: props.userId,
            certification_master_data: nodeList,
            file_data: fileData
          }
        },
        headers: {}
      }

      const res = await httpPost(
        restApiKey.CHROFY_REST_API,
        PathInfoList.Resource.updateCertificationMaster(props.corporationNumber),
        init
      ).catch(() => {
        throw new Error(errorMessage)
      })

      if (res?.message?.[0] !== 'Success') {
        throw new Error(errorMessage)
      }
      const successMessage = 'データ反映を開始しました。'
      props.navigate('/master/masterList', { state: { message: successMessage } })
    } catch (error) {
      console.log(error)
      // 業務エラーは日本語をそのまま、英語は E011012 へ（バグ #3228）
      const displayMessage = error?.message === errorMessage ? error.message : getMessage('E011012')
      setServerError({ errorMessage: displayMessage })
      errorMessageRef?.current.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
      _closeUpModal()

      errorLog('E000001', props.userId, viewId.certificationInfo, 'certificationInfo', error)
    }
  }

  const handleDragStart = (e) => {
    setActiveNode(e.active.id)
  }

  /**
   * @function ドラッグの処理
   * @param {ModifyTree} m
   */
  const dragLogic = (m, { activeId, activeParent, activeDepth }, { overId, overParent, overDepth }) => {
    const sameParent = activeParent === overParent
    const sameDepth = activeDepth === overDepth

    if (activeParent === overId) {
      const topNode = m.getSortedSubNodes(overId)
      m.updateSortOrder(activeId, overId, topNode[0].id)
      return
    }

    if (sameDepth && sameParent) {
      // 同じ深さと親の場合表示順を更新 sortOrder to　displayorderは同じ意味
      m.updateSortOrder(activeId, activeParent, overId)
    } else if (sameDepth && !sameParent) {
      // 同じ深さと違う親の場合
      // 親の元にターゲットを追加 , 元の親から削除 と親の値を更新
      m.addChild(activeId, overParent).removeChild(activeId, activeParent).setParent(activeId, overParent)
      // 表示順を更新
      m.updateSortOrder(activeId, overParent, overId)
    } else if (!sameDepth && sameParent) {
      // 親の元にターゲットを追加 , 元の親から削除 と親の値を更新
      if (activeDepth > overDepth) {
        m.addChild(activeId, overId).removeChild(activeId, activeParent).setParent(activeId, overId)
        m.updateSortOrder(activeId, overId, overId)
      } else {
        m.updateSortOrder(activeId, overParent, overId)
      }
      // 表示順を更新
    } else if (!sameDepth && !sameParent) {
      if (activeDepth > overDepth) {
        // 上の階層からしたに移行を防ぐため
        if (overId === activeParent) {
          m.addChild(activeId, overId).removeChild(activeId, activeParent)
        } else {
          m.addChild(activeId, overId).removeChild(activeId, activeParent).setParent(activeId, overId)
        }

        m.updateSortOrder(activeId, overId, overId)
      }
    }
  }

  /** @function ドラッグの処理 */
  function handleDragEnd(e) {
    const { active, over } = e

    // IDから要らないテキストを削除
    const sanitize = (id) => id.replaceAll(/drag_|drop_/g, '')
    const [activeId, overId] = [active.id, over.id].map(sanitize)

    if (overId === activeId) return

    const tree = { ...certificationMasterTree }
    const m = new ModifyTree(tree)

    // 親ノードID
    const activeParent = m.getParent(activeId) // 親
    const overParent = m.getParent(overId) // 親

    const activeDepth = m.getNode(activeId).Metadata.Depth
    const overDepth = m.getNode(overId).Metadata.Depth

    const isNoCategoryFamily = overId === noCategoryKey || overParent === noCategoryKey

    if (isNoCategoryFamily && activeDepth === 2) return

    dragLogic(m, { activeId, activeParent, activeDepth }, { overId, overParent, overDepth })
    setCertificationMasterTree(tree)
  }

  async function handleUploadCSV(e) {
    const file = e.target.files[0]

    const csv = await readCsvFile(file)

    const data = csv.slice(1)

    // name id

    const categoryMap = {}
    const subCategoryMap = {}

    //
    const certification = {}
    const temp = { ...certificationMasterTree }

    for (const item of data) {
      const [, code, name, main, sub, validity, hasScore] = item

      if (!main) continue
      if (categoryMap[main]) continue

      const id = addNodeToTree(temp)(
        rootNodeKey,
        {
          CategoryName: main
        },
        [],
        1
      )

      categoryMap[main] = id
    }

    for (const item of data) {
      const [, , , main, sub] = item

      if (!sub) continue
      if (sub === '(サブカテゴリなし)' || sub === '-') continue
      if (subCategoryMap[sub]) continue

      const subId = addNodeToTree(temp)(
        noCategoryKey,
        {
          CategoryName: sub
        },
        [],
        2
      )

      subCategoryMap[sub] = subId
      temp[subId].Metadata.SuperNode = categoryMap[main]
    }
    for (const item of data) {
      const [, code, name, main, sub, validity, hasScore] = item

      if (!name) continue

      const certid = addNodeToTree(temp)(
        noCategoryKey,
        {
          CertificationCode: code,
          Name: name,
          HasScore: hasScore !== '' && hasScore === '1',
          ValidityPeriod: validity
        },
        [],
        3
      )

      certification[name] = certid
      temp[certid].CertificationCode = code
      temp[certid].Metadata.SuperNode =
        sub === '(サブカテゴリなし)' || sub === '-' ? categoryMap[main] : subCategoryMap[sub]
    }

    for (const key in temp) {
      const parentId = temp[key].Metadata.SuperNode

      if (key === rootNodeKey) continue
      if (parentId === rootNodeKey) continue
      if (!temp[parentId]) continue

      temp[parentId].SubNode.push(key)
    }
    temp[noCategoryKey].SubNode = []

    setCertificationMasterTree(temp)
  }

  /** 描画処理 */
  if (!isLoaded) {
    return null
  }

  return (
    <>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link color="inherit" variant="button" underline="none" component={RouterLink} to="/master/masterList">
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>{' '}
                  </span>
                </Link>
              </div>
            </div>

            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">
                {masterClassification?.[master?.Classification]?.name}
              </h1>
            </header>

            <div ref={errorMessageRef}>
              <span style={{ color: 'red' }}>{inputError.errorMessage}</span>
              <span style={{ color: 'red' }}>{serverError.errorMessage}</span>
            </div>
            <div className="page-body">
              <div className="form-control">
                <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1">
                  <div className="content-area base-table-header">
                    <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                      資格マスター設定
                    </h2>
                    <div className="flex items-center justify-between my-6">
                      資格情報を登録してください。
                      <br />
                      資格を分類するカテゴリーを設定してから、その配下に資格を登録してください。
                      {window?.debug_ && <input type="file" style={{ opacity: 0 }} onChange={handleUploadCSV} />}
                    </div>
                    <div className="flex justify-between gap-x-5 mb-3 items-center btn-group !float-none">
                      <span className="text-body-2-medium grow  border-r border-blue2-lighten-20">■資格一覧</span>

                      <Link
                        className="header-btn !p-0 "
                        color="inherit"
                        variant="button"
                        underline="none"
                        component={'button'}
                        onClick={() => onAddNodeToParent(null, 'category')}
                      >
                        <span className="header-btn white--text py-1.5 pl-7 pr-10 inline-flex items-center">
                          <span className="icon-plus-line icon-32px mr-1.5 my-px"></span>
                          <span className="-translate-y-px text-h7">メインカテゴリー追加</span>
                        </span>
                      </Link>
                    </div>

                    <DndContext
                      collisionDetection={closestCorners}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <CertificationTree
                        tree={certificationMasterTree}
                        nodeId={rootNodeKey}
                        onAddNode={onAddNodeToParent}
                        onRemoveNode={onRemoveNode}
                        onUpdateNode={onUpdateNode}
                        onFold={onFold}
                      />

                      <DragOverlay>
                        {activeNode && (
                          <div className="border-2 border-solid border-red-500">
                            <CertificationTree
                              tree={certificationMasterTree}
                              nodeId={activeNode.replaceAll(/drag_|drop_/g, '')}
                              onAddNode={onAddNodeToParent}
                              onRemoveNode={onRemoveNode}
                              onUpdateNode={onUpdateNode}
                              onFold={onFold}
                              isActive
                              isClosed
                            />
                          </div>
                        )}
                      </DragOverlay>
                    </DndContext>

                    <footer className="content-footer">
                      <div className="border-t border-blue1"></div>
                      <div className=" footer_button_area flex justify-around gap-x-5 mt-5">
                        <button onClick={handleCancel} className="footer-btn button_back">
                          キャンセル
                        </button>

                        <SpinnerButton
                          name="登録"
                          className={'footer-btn button_base '}
                          onClick={handleSaveCertification}
                        />
                      </div>
                    </footer>
                  </div>
                </div>
              </div>
              {/*  ===== 関連ダッシュボード ===== */}
              {master?.RelatedDashboard && (
                <RelatedDashboard dashboards={master?.RelatedDashboard} authority={props.authority} mode={props.mode} />
              )}
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

export const ShowErrors = ({ errors: errorMessage, field = '' }) => {
  if (!errorMessage?.[field]) return <></>

  return (
    <>
      {errorMessage[field].map((err, index) => (
        <p key={index} className="text-red-500 text-sm">
          {err}
        </p>
      ))}
    </>
  )
}

// カテゴリのモダル
/** @type {ReturnType<typeof React.forwardRef<CategoryModalContentProps>>} CategoryModalContent */
export const CategoryModalContent = forwardRef((props, ref) => {
  const { editMode, parentNode, categoryList = [], defaultData, isMainCategory, onClose, onSubmit } = props

  const initial = Object.freeze({ CategoryName: '', SuperNode: parentNode?.node || '' })

  const [formData, setFormData] = useState(defaultData || initial)
  const [errors, setErrorState] = useState({})

  const formHasErrors = (formData) => {
    let errors = {}

    const CategoryName = formData.CategoryName
    const SuperNode = formData.SuperNode

    if (CategoryName.trim() === '') {
      errors = { ...errors, CategoryName: [getMessage('E010001', 'カテゴリー名')] }
    }

    if (isMainCategory && categoryList.find((e) => e.CertificationData.CategoryName === CategoryName.trim())) {
      errors = { ...errors, CategoryName: [getMessage('E010013', '同一の「メインカテゴリー名」')] }
    }

    if (SuperNode.trim() === '') {
      errors = { ...errors, SuperNode: getMessage('E010017', 'メインカテゴリー') }
    }

    const allFields = Object.entries(formData).filter((e) => !['node', 'memo'].some((x) => x === e[0]))

    allFields.forEach((field) => {
      if (checkIfCharsExist(field[1] || '')) {
        const charString = disallowedChars.join(' ')

        errors = { ...errors, [field[0]]: [getMessage('E010016', charString)] }
      }
    })

    if (Object.keys(errors).length > 0) return errors

    return false
  }

  const { CategoryName } = formData

  const updateValue = (field) => (e) => {
    setFormData((s) => ({
      ...s,
      [field]: e.target.value
    }))
  }

  const prefix = isMainCategory ? 'メイン' : 'サブ'

  const resetForm = () => {
    setFormData(initial)
  }

  const _handleSubmit = () => {
    const errors = formHasErrors(formData)

    if (errors) {
      setErrorState(errors)
      return
    }

    setErrorState({})

    onSubmit?.({ ...formData, Depth: isMainCategory ? 1 : 2 }, setErrorState, resetForm)
  }

  useLayoutEffect(() => {
    const modalWindowElement = document.querySelector('.modal-window')

    const classList = ['max-w-[480px]', 'max-h-[85dvh]']
    modalWindowElement.classList.add(...classList)
    return () => modalWindowElement.classList.remove(...classList)
  }, [])

  const categoryOptions = categoryList.map((e) => ({
    label: _get(e, 'CertificationData.CategoryName', ''),
    value: e.node
  }))

  return (
    <>
      <form
        ref={ref}
        className=" text-base "
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <table className="form-table table-fixed w-full">
          <tbody className="text-caption-2">
            {!isMainCategory && (
              <tr>
                <th className="form-table__col-head w-[150px]">
                  <label className="text-h7 font-bold">親カテゴリー</label>
                </th>
                <td className="form-table__col-data form-control">
                  {!editMode && (
                    <ChrofyCustomSelect
                      className="w-full  text-h7"
                      name="SuperNode"
                      value={formData.SuperNode}
                      options={categoryOptions}
                      onChange={updateValue('SuperNode')}
                      isError={!!errors?.SuperNode?.length}
                    >
                      {categoryList.map((category, key) => {
                        return (
                          <option key={`select_category_${key}`} value={category.node}>
                            {category?.CertificationData?.CategoryName || ''}
                          </option>
                        )
                      })}
                    </ChrofyCustomSelect>
                  )}
                  {editMode && <span>{parentNode?.CertificationData?.CategoryName}</span>}

                  {<ShowErrors errors={errors} field="SuperNode" />}
                </td>
              </tr>
            )}

            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="CategoryName" className="text-h7 font-bold">
                  カテゴリー名 <span className="red--text">*</span>
                </label>
              </th>
              <td className="form-table__col-data">
                <input
                  id="CategoryName"
                  name="CategoryName"
                  className="text-input w-full text-left min-h-[46px] "
                  type="text"
                  value={CategoryName || ''}
                  onChange={updateValue('CategoryName')}
                  placeholder="カテゴリー名を入力してください"
                />
                <ShowErrors errors={errors} field="CategoryName" />
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      <div className="modal-content-footer mt-8">
        <div className="footer_button_area gap-x-16">
          <button className="footer-btn button_back " onClick={() => onClose?.(resetForm)}>
            キャンセル{' '}
          </button>
          <SpinnerButton
            name="設定"
            className={'bg-black px-2 py-2 text-white min-w-[80px] rounded w-[160px]'}
            onClick={_handleSubmit}
          />
        </div>
      </div>
    </>
  )
})

/**
 * @function FormInput
 * @param {FormInputProps} props
 */
const FormInput = ({ id, label, required = true, placeholder, value, errors, disabled, onChange }) => {
  return (
    <tr>
      <th className="form-table__col-head w-[150px]">
        <label htmlFor={id} className="text-h7 font-bold">
          {label} {required && <span className="red--text">*</span>}
        </label>
      </th>
      <td className="form-table__col-data">
        <input
          id={id}
          name={id}
          className="text-input w-full text-left"
          type="text"
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
        />
        <ShowErrors field={id} errors={errors} />
      </td>
    </tr>
  )
}

// カテゴリのモダル
export const CertificationModalContent = forwardRef((props, ref) => {
  const { tree, node, editMode, defaultData, onClose, onSubmit } = props

  const initial = { node, CertificationCode: '', Name: '', ValidityPeriod: '', HasScore: false, memo: '' }

  const initData = {
    ...initial,
    ...defaultData
  }
  useLayoutEffect(() => {
    const modalWindowElement = document.querySelector('.modal-window')
    const modalWindowClassList = ['w-[initial]', 'max-h-[97vh]', 'max-w-[750px]', 'overflow-y-auto']

    modalWindowElement.classList.add(...modalWindowClassList)
    return () => {
      modalWindowElement.classList.remove(...modalWindowClassList)
    }
  }, [])

  const [formData, setFormData] = useState(initData)
  const [errors, setErrorState] = useState({})

  /** @function バリデーション条件資格 */
  const formHasErrors = () => {
    const errors = {
      CertificationCode: [],
      Name: [],
      ValidityPeriod: [],
      memo: []
    }

    if (formData.CertificationCode?.trim() === '') {
      errors.CertificationCode.push(getMessage('E010001', '資格コード'))
    }

    const predicate = (e) => {
      let exists = e.CertificationCode === formData?.CertificationCode

      if (editMode) {
        exists = exists && e.node !== node
      }
      return exists
    }

    if (Object.values(tree).find(predicate)) {
      errors.CertificationCode.push(getMessage('E010013', '同一の「資格コード」'))
    }

    if (formData?.Name?.trim() === '') {
      errors.Name.push(getMessage('E010001', '資格名'))
    }

    const validityPeriod = formData?.ValidityPeriod

    if (validityPeriod && !/^\d+$/.test(validityPeriod)) {
      errors.ValidityPeriod?.push('整数値を入力してください。')
    }

    if (validityPeriod !== '' && validityPeriod !== null && Number(validityPeriod) < 1) {
      errors.ValidityPeriod.push(getMessage('E010010', '有効期限', '1'))
    }

    if ((formData.memo || '').trim().length > 200) {
      errors.memo.push(getMessage('E010006', 'メモ', '200'))
    }

    const allFields = Object.entries(formData).filter((e) => !['node', 'memo', 'HasScore'].some((x) => x === e[0]))

    allFields.forEach((field) => {
      if (checkIfCharsExist(field?.[1] || '')) {
        const charString = disallowedChars.join(' ')
        errors[field[0]].push(getMessage('E010016', charString))
      }
    })

    if (Object.values(errors).some((e) => e.length > 0)) return errors

    return false
  }

  /** @function フォームをリセット */
  const resetForm = () => {
    setFormData(initial)
  }

  const updateValue = (field) => (e) => {
    let value = typeof e === 'string' ? e : e.target.value

    if (e?.target?.type === 'checkbox') {
      value = e.target.checked
    }

    setFormData((s) => ({ ...s, [field]: value }))
  }

  const _handleSubmit = () => {
    const errors = formHasErrors()

    if (errors) {
      setErrorState(errors)
      return
    }

    setErrorState({})
    onSubmit?.(formData, resetForm, setFormData)
  }

  /** @type {ReturnType<typeof React.useRef<HTMLInputElement | null>>} */
  const validityPeriodRef = React.useRef(null)

  useEffect(() => {
    const validityPeriod = validityPeriodRef.current

    if (!validityPeriod) return

    function handleFocus(e) {
      const value = e.target.value?.trim()
      validityPeriod.value = parseCommaDigit(value)
      validityPeriod.dataset.noFormat = parseCommaDigit(value)
    }
    function handleBlur(e) {
      validityPeriod.value = formatCommaDigit(e.target.dataset.noFormat)
      validityPeriod.dataset.noFormat = parseCommaDigit(validityPeriod.value)

      if (Number(validityPeriod.dataset.noFormat) < 0) {
        validityPeriod.value = ''
        validityPeriod.dataset.noFormat = ''
      }

      updateValue('ValidityPeriod')(validityPeriod.dataset.noFormat)
    }

    function handleInput(e) {
      const value = e.target.value?.trim()
      validityPeriod.dataset.noFormat = parseCommaDigit(value)
    }

    validityPeriod.addEventListener('focus', handleFocus)
    validityPeriod.addEventListener('blur', handleBlur)
    validityPeriod.addEventListener('input', handleInput)

    return () => {
      validityPeriod.removeEventListener('focus', handleFocus)
      validityPeriod.removeEventListener('blur', handleBlur)
      validityPeriod.removeEventListener('input', handleInput)
    }
  }, [])

  const handleValidityPeriodChange = (e) => {
    updateValue('ValidityPeriod')(e.target.dataset.noFormat)
  }

  return (
    <>
      <form ref={ref} className=" text-base ">
        <table className="form-table table-fixed w-full">
          <tbody>
            {/* =======================資格コード====================== */}
            <FormInput
              errors={errors}
              value={formData.CertificationCode}
              onChange={updateValue('CertificationCode')}
              id="CertificationCode"
              label="資格コード"
              placeholder="資格を識別するコードを入力してください"
              disabled={editMode}
            />
            {/* =======================資格コード====================== */}

            {/* =======================資格名====================== */}
            <FormInput
              errors={errors}
              value={formData.Name}
              onChange={updateValue('Name')}
              id="Name"
              label="資格名"
              placeholder="資格名を入力してください"
            />
            {/* =======================資格名====================== */}

            {/* =======================有効期間====================== */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor={'ValidityPeriod'} className="text-h7 font-bold">
                  有効期間
                </label>
              </th>
              <td className="form-table__col-data">
                <input
                  ref={validityPeriodRef}
                  id={'ValidityPeriod'}
                  data-testid="ValidityPeriod"
                  name={'ValidityPeriod'}
                  maxLength={5}
                  className="text-input w-32 text-left mr-3 format-digit"
                  type="text"
                  defaultValue={formData.ValidityPeriod || ''}
                  data-no-format={formData.ValidityPeriod || ''}
                  onChange={handleValidityPeriodChange}
                  placeholder={'整数値を入力'}
                />
                <span>ヶ月（※未入力の場合は無期限）</span>

                <ShowErrors field="ValidityPeriod" errors={errors} />
              </td>
            </tr>
            {/* =======================有効期間====================== */}

            {/* =======================点数有無====================== */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor={'HasScore'} className="text-h7 font-bold">
                  点数有無
                </label>
              </th>
              <td className="form-table__col-data">
                <Checkbox
                  id="HasScore"
                  size="small"
                  checkedIcon={<CheckboxRountedIcon />}
                  checked={formData.HasScore}
                  onChange={updateValue('HasScore')}
                />
                <span>(点数を保持する資格の場合はチェックをONにしてください)</span>

                <ShowErrors field="HasScore" errors={errors} />
              </td>
            </tr>
            {/* =======================点数有無====================== */}

            {/* =======================メモ====================== */}
            <tr>
              <th className="form-table__col-head w-[150px]">
                <label htmlFor="memo" className="text-h7 font-bold">
                  メモ
                </label>
              </th>
              <td className="form-table__col-data">
                <textarea
                  className="modal-window__content-textarea rounded-lg px-4 py-3 w-full h-auto min-h-[120px]"
                  placeholder="メモを入力してください（全角200文字程度）"
                  maxLength={200}
                  id="memo"
                  name="memo"
                  value={formData.memo}
                  onChange={updateValue('memo')}
                ></textarea>

                <ShowErrors field={'memo'} errors={errors} />
              </td>
            </tr>
            {/* =======================メモ====================== */}
          </tbody>
        </table>
      </form>

      <div className="modal-content-footer mt-3">
        <div className="footer_button_area gap-x-16">
          <button className="footer-btn button_back " onClick={() => onClose?.(resetForm)}>
            キャンセル{' '}
          </button>
          <SpinnerButton
            name="設定"
            className={'bg-black px-2 py-2 text-white min-w-[80px] rounded w-[160px]'}
            onClick={_handleSubmit}
          />
        </div>
      </div>
    </>
  )
})

// ツリー表示
/**
 * @function
 * @param {CertificationTreeProps} props
 */
export const CertificationTree = (props) => {
  const {
    tree,
    nodeId,
    isClosed = false,
    isActive = false,
    parentDepth = null,
    onAddNode,
    onRemoveNode,
    onUpdateNode,
    onFold
  } = props

  const node = tree[nodeId]
  if (!node) return <></>

  const fold = isClosed || node.state.fold
  const editable = Boolean(node.state.editable)
  // 算出
  const childNodes = node.SubNode.sort((a, b) => tree[a]?.Metadata?.DisplayOrder - tree[b]?.Metadata?.DisplayOrder)
  // ノードのデータのため
  const nodeData = node.CertificationData
  // ノードの 階層
  const nodeDepth = node.Metadata.Depth
  // ノードのヘッダーの色
  const showHeader = node.Metadata.Depth === 1
  const border = 'border-b-2 border-blue-200 border-solid'
  // ノードが削除されているかのフラッグ
  const isNodeDeleted = node.state?.deleted
  // カテゴリなしのチェック
  const isNodeNoCategory = nodeId === noCategoryKey
  // サブカテゴリの表示条件
  const showSubCategoryAddButton = nodeDepth === 1 && !isNodeNoCategory
  // ノード追加ボタンの表示条件
  const showAddNode = nodeDepth < 3
  // サブカテゴリ追加の表示
  const showAddSubCategory = showHeader && !isNodeDeleted && !fold && !isNodeNoCategory

  const colorPerDepth = ['bg-[#e0e5f2]', 'bg-[#e0e5f245] ', 'bg-transparent']
  const nodeBg = useMemo(() => {
    let bg = ''
    if (nodeId === rootNodeKey) return ''
    bg = bg.concat(colorPerDepth[nodeDepth - 1])

    if (showHeader) bg = bg.concat(' font-bold  !text-base ')
    if (isNodeDeleted) bg = bg.concat(' !bg-gray-300 ')

    return bg
  }, [showHeader, node, isNodeDeleted])

  const foldableProps = { isFoldable: childNodes.length > 0, border, nodeBg, fold }
  // 算出
  const DeleteButtonComponent = ({ onRemoveNode, isNodeDeleted }) => {
    const beforeClasses = [
      'before:absolute',
      'before:block',
      "before:content-['']",
      'before:h-[30px]',
      'before:w-[30px]',
      'before:z-10',
      'before:rounded-full',
      'before:hidden',
      'before:bg-gray-600/10'
    ]
    const before = beforeClasses.join(' ')

    if (!node || isNodeNoCategory) return <i className="w-[20px]"></i>

    if (isNodeDeleted) {
      return (
        <IconButton
          title="資格削除"
          size="small"
          className={`!p-0 relative !text-gray-600/50 ${before} hover:before:block  `}
          onClick={onRemoveNode}
        >
          <UndoIcon className={'w-[20px] h-[20px]'} />
        </IconButton>
      )
    }

    return (
      <IconButton
        title="復帰"
        size="small"
        className={`!p-0 relative !text-gray-600/50 ${before} hover:before:block  `}
        onClick={onRemoveNode}
        data-testid="delete"
      >
        <DeleteIcon className="z-20" fontSize="small" />
      </IconButton>
    )
  }

  const Foldable = ({ isFoldable, fold }) => {
    const className = 'h-full  w-10 flex justify-center items-center'

    if (!isFoldable) return <span className={`${className}`}></span>

    return (
      <button disabled={isNodeDeleted} className={`${className}`} onClick={() => onFold(nodeId)} data-testid="foldable">
        <ArrowDropDown
          sx={{
            transition: 'all 200ms ease-in-out',
            transform: fold ? 'rotate(270deg)' : 'rotate(360deg)'
          }}
        />
      </button>
    )
  }

  const indentDiff = nodeDepth - parentDepth ?? 0

  const fillIndent = [
    'before:content-[""] before:block before:h-[2px] ',
    `before:w-[calc(${indentDiff}*4%+3px)] `,
    'before:absolute before:top-[100%] before:right-[100%] before:bg-blue-200'
  ].join(' ')

  const before = parentDepth && nodeDepth > 1 ? fillIndent : ''

  const name = nodeData?.CategoryName || nodeData?.Name || ''

  return (
    <>
      <Draggable
        disabled={isNodeDeleted}
        style={{
          marginLeft: nodeDepth > 1 && !isActive ? `calc(${nodeDepth - 1} * 4%)` : undefined
        }}
        className={'relative'}
        id={nodeId}
        tree={tree}
        data-testid={`${name}_${nodeDepth}`}
      >
        {(listeners) => {
          return (
            <>
              {nodeId !== rootNodeKey && (
                <div className={`${border} ${nodeBg} relative text-sm flex items-center h-12`}>
                  {/** 畳 */}
                  {<Foldable {...foldableProps} />}
                  {/** 畳 */}
                  <Droppable
                    isDroppable={nodeDepth < 3}
                    id={nodeId}
                    className={'flex flex-1 gap-x-3  '}
                    title={window.debug_ && pJson(node)}
                  >
                    <button data-testid="dragControl" className={''} {...listeners}>
                      ≡
                    </button>
                    <div className={'flex gap-x-5 p-2 flex-1 items-center '}>
                      <a
                        role="button"
                        className={`text-blue-500  cursor-pointer  flex items-center flex-1  underline-offset-4  ${
                          isNodeDeleted && 'line-through text-black'
                        } `}
                        onClick={
                          !isNodeDeleted && editable
                            ? (e) => {
                                e.stopPropagation()
                                onUpdateNode(nodeId)
                              }
                            : null
                        }
                      >
                        {name}
                        {nodeDepth < 3 && (
                          <CategoryIcon className="w-[20px] h-[20px] ml-2 mt-px inline-block text-current " />
                        )}
                      </a>

                      {showSubCategoryAddButton && (
                        <button
                          title="サブカテゴリーを追加"
                          data-testid="sub_add"
                          disabled={isNodeDeleted}
                          className={` header-btn px-2 inline-flex items-center text-xs bg-[#297ad8] text-white rounded font-normal ${
                            isNodeDeleted && 'bg-gray-400/50'
                          } `}
                          onClick={() => onAddNode(nodeId, 'category')}
                        >
                          <i className="icon-plus-line text-xl mr-1 my-px"></i>サブカテゴリー追加
                        </button>
                      )}

                      {showAddNode ? (
                        <button
                          title="資格を追加"
                          data-testid="cert_add"
                          disabled={isNodeDeleted}
                          className={` !text-sm !p-0 inline-flex items-center text-blue-500  ${
                            isNodeDeleted && 'text-gray-500/60'
                          } `}
                          onClick={() => onAddNode(nodeId)}
                        >
                          <i className="icon-plus-line text-xl mr-1 my-px"></i>
                          <span className="font-normal text-xs">資格を追加</span>
                        </button>
                      ) : (
                        <span className={`${fold && 'hidden'}`}></span>
                      )}

                      <DeleteButtonComponent isNodeDeleted={isNodeDeleted} onRemoveNode={() => onRemoveNode(nodeId)} />
                    </div>
                  </Droppable>
                </div>
              )}
            </>
          )
        }}
      </Draggable>

      {!fold && (
        <ProgressiveLoader
          node={node.node}
          tree={tree}
          items={childNodes}
          renderer={(childId, index) => {
            return (
              <CertificationTree
                tree={tree}
                key={childId}
                nodeId={childId}
                onAddNode={onAddNode}
                onUpdateNode={onUpdateNode}
                onRemoveNode={onRemoveNode}
                onFold={onFold}
                isActive={isActive}
                parentDepth={index === childNodes.length - 1 ? nodeDepth : null}
              />
            )
          }}
        />
      )}
    </>
  )
}

/**
 * 配列を分解します
 *
 * @param {string[]} items
 * @param {number} chunkSize
 * @returns
 */
function chunkArray(items, chunkSize = 20) {
  const chunks = []
  for (let i = 0; i < items.length; i += chunkSize) {
    const min = Math.min(items.length, i + chunkSize)
    chunks.push(items.slice(i, min))
  }
  return chunks
}
/**
 * ディレイでアイテム表示
 *
 * @param {any} chunks
 * @param {any} renderDelay
 */
async function* generateNodes(chunks, renderDelay) {
  while (chunks.length > 0) {
    const value = chunks.shift()
    if (value) yield value
    await new Promise((resolve) => setTimeout(resolve, renderDelay))
  }
}

const ProgressiveLoader = (props) => {
  const { items, renderDelay = 200, renderer, tree } = props

  const [chunkedItems, setChunkedItems] = React.useState([])
  const completeRenderRef = React.useRef(false)

  const sortPredicate = (a, b) => tree[a]?.Metadata?.DisplayOrder - tree[b]?.Metadata?.DisplayOrder

  const chunks = React.useMemo(() => chunkArray(items), [items])

  useEffect(() => {
    const generator = generateNodes(chunks, renderDelay)

    const loop = async () => {
      const { value, done } = await generator.next()

      if (!done) {
        setChunkedItems(
          produce((draft) => {
            const valueSet = new Set(draft)
            value.forEach(valueSet.add, valueSet)
            return Array.from(valueSet)
          })
        )
        await loop()
      }
    }

    loop().then(() => {
      completeRenderRef.current = true
    })
    return () => generator.return()
  }, [chunks])

  return <>{(completeRenderRef.current ? items : chunkedItems).slice().sort(sortPredicate).map(renderer)}</>
}

const CertificationInfo = () => {
  const state = useSelector((state) => state)
  const location = useLocation()
  const showToast = useToast()
  const showModal = useModal()

  const navigate = useNavigate()

  if (
    !state.authority.Master_AuthInfo.Availability ||
    !state.authority.Master_AuthInfo.Auth_Conf.Certification.CertificationMaster_Set
  ) {
    return <Forbidden />
  }

  return (
    <CertificationContent
      pageTitle={titleName.certificationInfo + titleName.common}
      corporationNumber={state.company.corporationNumber}
      corporationName={state.company.name}
      userId={state.user.sub}
      userName={state.user.userName}
      isParent={state.company.isParent}
      message={location.state?.message}
      errorMessages={location.state?.errorMessages}
      showToast={showToast}
      mode={state.analysis.mode}
      showModal={showModal}
      navigate={navigate}
      authority={state.authority}
    />
  )
}

export default CertificationInfo
